import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import recordEvent from 'platform/monitoring/record-event';
import { focusElement } from 'platform/utilities/ui';
import { VaModal } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

// Utils
import { clearSearchResults, clearSearchText } from '../../actions/search';
import {
  GEOCODE_SERVICE_UNAVAILABLE,
  REVERSE_GEOCODE_FAILED,
  clearGeocodeError,
  genBBoxFromAddress,
  geolocateUser,
} from '../../actions/mapbox';
import { getProviderSpecialties } from '../../actions/locations';
import { setFocus } from '../../utils/helpers';
import { SearchFormTypes } from '../../types';

// Hooks
import useSearchFormState from '../../hooks/useSearchFormState';
import useSearchFormSync from '../../hooks/useSearchFormSync';
import useSearchSubmit from '../../hooks/useSearchSubmit';

// Components
import FacilityType from './facility-type';
import ServiceType from './service-type';
import AddressAutosuggest from './location/AddressAutosuggest';

/** Draft state pattern: inputs update local state, commit to Redux on submit. */
export const SearchForm = props => {
  const {
    currentQuery,
    isMobile,
    isSmallDesktop,
    isTablet,
    mobileMapUpdateEnabled,
    onChange,
    onSubmit,
    searchInitiated,
    selectMobileMapPin,
    setSearchInitiated,
    suppressPPMS,
    vamcAutoSuggestEnabled,
  } = props;

  const {
    draftFormState,
    setDraftFormState,
    updateDraftState,
    handleFacilityTypeChange,
    handleServiceTypeChange,
    selectedServiceType,
    setSubmitErrors,
    clearSubmitErrors,
  } = useSearchFormState(currentQuery);

  // Synchronize URL params, Redux, and draft state
  useSearchFormSync({
    currentQuery,
    draftFormState,
    setDraftFormState,
    updateDraftState,
    location: props.location,
    onChange,
    vaHealthServicesData: props.vaHealthServicesData,
  });

  // Track geolocation errors for analytics
  useEffect(() => {
    if (currentQuery?.geocodeError) {
      if (currentQuery.geocodeError === 1) {
        recordEvent({
          event: 'fl-get-geolocation-permission-error',
          'error-key': '1_PERMISSION_DENIED',
        });
      } else {
        const errorKeyByCode = {
          2: '2_POSITION_UNAVAILABLE',
          3: '3_TIMEOUT',
          [REVERSE_GEOCODE_FAILED]: '4_REVERSE_GEOCODE_FAILED',
          [GEOCODE_SERVICE_UNAVAILABLE]: '5_GEOCODE_SERVICE_UNAVAILABLE',
        };
        recordEvent({
          event: 'fl-get-geolocation-other-error',
          'error-key': errorKeyByCode[currentQuery.geocodeError] || '3_TIMEOUT',
        });
      }
    }
  }, [currentQuery.geocodeError]);

  // Confirms a location is real via the geocoder without starting a search —
  // used when facility/service type is still invalid so a fake-but-well-
  // formed location (e.g. ZIP "00000") is caught on the first submit
  // instead of requiring a second one (CMS #24836).
  // Passes along the feature the address autosuggest already resolved for this
  // string when there is one, so the check costs no request (CMS #20560).
  const checkLocationValidity = (searchString, feature) =>
    props.genBBoxFromAddress({ searchString }, { validateOnly: true, feature });

  const { handleSubmit } = useSearchSubmit({
    draftFormState,
    setDraftFormState,
    selectedServiceType,
    currentQuery,
    location: props.location,
    onChange,
    onSubmit,
    isMobile,
    mobileMapUpdateEnabled,
    selectMobileMapPin,
    setSearchInitiated,
    setSubmitErrors,
    clearSubmitErrors,
    clearSearchResults: props.clearSearchResults,
    checkLocationValidity,
  });

  const locationInputFieldRef = useRef(null);
  // Tracks whether a real "Use my location" geocode was actually kicked off,
  // so the effect below only refocuses the location field for that flow's
  // true→false completion — not for the unrelated false transition that
  // GEOCODE_COMPLETE/GEOCODE_FAILED also apply as a blanket reset. Without
  // this, the very first background validate-only check (CMS #24836) flips
  // geolocationInProgress from its unset (undefined) initial value to
  // false, which looks like a fresh completion and steals focus away from
  // whichever field (e.g. facility type) the user was just sent to.
  const geolocationWasInProgressRef = useRef(false);

  const handleGeolocationButtonClick = e => {
    e.preventDefault();
    recordEvent({ event: 'fl-get-geolocation' });
    props.geolocateUser();
  };

  const handleClearInput = () => {
    props.clearSearchText();
    updateDraftState({ searchString: '', locationGeocode: null });
    if (locationInputFieldRef.current) {
      locationInputFieldRef.current.value = '';
    }
    focusElement('#street-city-state-zip');
  };

  // Set focus in the location field when manual geocoding completes
  useEffect(() => {
    if (currentQuery.geolocationInProgress === true) {
      geolocationWasInProgressRef.current = true;
      return;
    }
    if (
      currentQuery.geolocationInProgress === false &&
      geolocationWasInProgressRef.current &&
      locationInputFieldRef.current
    ) {
      geolocationWasInProgressRef.current = false;
      setFocus(locationInputFieldRef.current, false);
    }
  }, [currentQuery.geolocationInProgress]);

  const facilityAndServiceTypeInputs = (
    <>
      <FacilityType
        currentQuery={draftFormState}
        handleFacilityTypeChange={handleFacilityTypeChange}
        isMobile={isMobile}
        isSmallDesktop={isSmallDesktop}
        isTablet={isTablet}
        suppressPPMS={suppressPPMS}
      />
      <ServiceType
        currentQuery={draftFormState}
        getProviderSpecialties={props.getProviderSpecialties}
        handleServiceTypeChange={handleServiceTypeChange}
        isMobile={isMobile}
        isSmallDesktop={isSmallDesktop}
        isTablet={isTablet}
        committedVamcServiceDisplay={
          draftFormState.vamcServiceDisplay ||
          (draftFormState.serviceType && currentQuery.vamcServiceDisplay)
        }
        onVamcDraftChange={updateDraftState}
        searchInitiated={searchInitiated}
        setSearchInitiated={setSearchInitiated}
        vamcAutoSuggestEnabled={vamcAutoSuggestEnabled}
      />
    </>
  );

  return (
    <div
      className={
        isSmallDesktop
          ? 'desktop-search-controls-container clearfix'
          : 'search-controls-container clearfix'
      }
    >
      <VaModal
        uswds
        modalTitle={(() => {
          if (currentQuery.geocodeError === 1) {
            return `Your device's location sharing is off.`;
          }
          if (currentQuery.geocodeError === REVERSE_GEOCODE_FAILED) {
            return "We couldn't match your location to an address";
          }
          if (currentQuery.geocodeError === GEOCODE_SERVICE_UNAVAILABLE) {
            return 'Our location service is unavailable';
          }
          return "We couldn't locate you";
        })()}
        onCloseEvent={() => props.clearGeocodeError()}
        status="warning"
        visible={currentQuery.geocodeError > 0}
      >
        <p>
          {(() => {
            if (currentQuery.geocodeError === 1) {
              return 'To use your location when searching for a VA facility, go to the settings on your device and update sharing permissions.';
            }
            if (currentQuery.geocodeError === REVERSE_GEOCODE_FAILED) {
              return 'We could not determine the location from your search. Please try a different zip code or city and state.';
            }
            if (currentQuery.geocodeError === GEOCODE_SERVICE_UNAVAILABLE) {
              return "We couldn't reach the service that finds your location. Check your connection and try again, or enter a city, state, or zip code.";
            }
            return 'Sorry, something went wrong when trying to find your location. Please make sure location sharing is enabled and try again.';
          })()}
        </p>
      </VaModal>

      <form id="facility-search-controls" onSubmit={handleSubmit}>
        <AddressAutosuggest
          currentQuery={{
            ...draftFormState,
            geolocationInProgress: currentQuery.geolocationInProgress,
            invalidLocation: currentQuery.invalidLocation,
          }}
          geolocateUser={handleGeolocationButtonClick}
          inputRef={locationInputFieldRef}
          isMobile={isMobile}
          isSmallDesktop={isSmallDesktop}
          isTablet={isTablet}
          onClearClick={handleClearInput}
          onChange={onChange}
          onLocationSelection={updateDraftState}
        />
        {facilityAndServiceTypeInputs}
        <va-button
          id="facility-search"
          onClick={handleSubmit}
          text="Search"
          full-width
        />
      </form>
    </div>
  );
};

const mapDispatchToProps = {
  clearGeocodeError,
  clearSearchResults,
  clearSearchText,
  genBBoxFromAddress,
  geolocateUser,
  getProviderSpecialties,
};

SearchForm.propTypes = SearchFormTypes;

export default connect(null, mapDispatchToProps)(SearchForm);
